import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WelcomeStep } from './WelcomeStep';
import { SynapseStep } from './SynapseStep';
import { trackEvent } from '../../lib/analytics';
import { useTranslation } from 'react-i18next';

interface OnboardingFlowProps {
  onComplete: () => Promise<void>;
  onDismiss: () => void;
  onAdvanceToInteractive: () => void;
}

export function OnboardingFlow({
  onComplete,
  onDismiss,
  onAdvanceToInteractive,
}: OnboardingFlowProps) {
  const { t } = useTranslation();
  const STEPS = [
    t('onboarding.step_welcome'),
    t('onboarding.step_synapse'),
    t('onboarding.step_first_entity'),
    t('onboarding.step_discovery'),
  ];
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const [stepKey, setStepKey] = useState(0);
  const navigate = useNavigate();

  const handleTrySynapse = useCallback(() => {
    trackEvent('onboarding_synapse_try');
    onDismiss();
    navigate('/synapse');
  }, [onDismiss, navigate]);

  const handleNext = useCallback(async () => {
    if (currentStep === 0) {
      setStepKey((k) => k + 1);
      setCurrentStep(1);
    } else if (currentStep === 1) {
      // Sortir de la modale, passer en mode interactif
      onAdvanceToInteractive();
    }
  }, [currentStep, onAdvanceToInteractive]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setStepKey((k) => k + 1);
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes onboarding-overlay-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes onboarding-container-in {
          from { opacity: 0; transform: scale(0.95) }
          to { opacity: 1; transform: scale(1) }
        }
        @keyframes onboarding-card-in {
          from { opacity: 0; transform: translateY(8px) }
          to { opacity: 1; transform: translateY(0) }
        }
        @keyframes onboarding-step-fade {
          from { opacity: 0 }
          to { opacity: 1 }
        }
      `}</style>

      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm"
        style={{ animation: 'onboarding-overlay-in 300ms ease-out' }}
      >
        <div
          className="relative max-w-2xl w-full mx-4 bg-zinc-900 border border-zinc-700/50 rounded-2xl shadow-2xl shadow-black/60 flex flex-col overflow-hidden"
          style={{ animation: 'onboarding-container-in 300ms ease-out' }}
        >
          {/* Decorative violet halo */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[80px] pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center top, rgba(138,43,226,0.12) 0%, transparent 70%)',
            }}
          />
          {/* Stepper */}
          <div className="flex items-center justify-center gap-8 px-8 pt-6 pb-2">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i === currentStep
                      ? 'bg-violet-500'
                      : i < currentStep
                        ? 'bg-violet-500/40'
                        : 'border border-zinc-600 bg-transparent'
                  }`}
                  style={
                    i === currentStep ? { boxShadow: '0 0 6px rgba(138,43,226,0.6)' } : undefined
                  }
                />
                <span
                  className={`text-xs transition-colors ${
                    i === currentStep ? 'text-zinc-100' : 'text-zinc-500'
                  }`}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Content */}
          <div
            key={stepKey}
            className="p-8 min-h-[400px] flex flex-col justify-center"
            style={{ animation: 'onboarding-step-fade 200ms ease-out' }}
          >
            {currentStep === 0 && <WelcomeStep />}
            {currentStep === 1 && <SynapseStep onTrySynapse={handleTrySynapse} />}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-8 pb-6">
            <button
              type="button"
              onClick={onDismiss}
              className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {t('common.skip')}
            </button>

            <div className="flex items-center gap-3">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="px-4 py-2 text-sm text-zinc-300 hover:text-zinc-100 transition-colors"
                >
                  {t('common.previous')}
                </button>
              )}
              {currentStep === 1 && (
                <button
                  type="button"
                  onClick={handleTrySynapse}
                  className="px-4 py-2 text-sm font-medium text-violet-400 border border-violet-500/40 hover:bg-violet-500/10 rounded-lg transition-colors"
                >
                  {t('onboarding.try_synapse')}
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors"
                style={{ boxShadow: '0 0 12px rgba(138,43,226,0.3)' }}
              >
                {t('common.continue')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
